/**
 * RegisterPage - Email entry for registration (Phase 1)
 *
 * Following CONVERSION_ERGONOMICS_RULEBOOK.md:
 * - Single email field only (maximum conversion)
 * - Social login for frictionless signup
 * - Clear value propositions
 * - Orange CTA button
 *
 * Following REACT_RULEBOOK.md:
 * - Using Store hooks only
 * - Proper provider hierarchy (handled by RouteRenderer)
 * - No custom state for business logic
 *
 * Following UX_RULEBOOK.md:
 * - Theme CSS variables
 * - 2-column layout (form left, marketing right) matching WelcomePage
 * - Consistent spacing and typography
 *
 * Legacy Reference: app/scripts/muiAuth/register/SignUp.js
 * Store: RegisterStore
 * FSM: InputtingEmail → Submitting → VerificationSent/Failed
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Store hooks FIRST (per REACT_RULEBOOK.md)
import { useRegisterStore } from "../../models/register";
import { SocialProvider } from "../../models/socialLogin";
import { useAppContextStore } from "../../models/appContext";

// UI components from tailwind-ui
import { Field, FieldLabel, FieldError, Input, Button, Text, Separator, Alert, AlertDescription } from '@innolab-trader/ui'
import { SocialButton } from '../../components'

// Shared components
import { FeatureCard, AuthPageLayout } from "../../components";

// Router components
import { Link } from "react-router-dom";

// Contexts
import { useTranslations } from "@innolab-trader/translations";

// Register page content component
function RegisterPageContent() {
  const T = useTranslations();
  const navigate = useNavigate();

  // Stores
  const { state, actions } = useRegisterStore();
  const appContext = useAppContextStore();
  const socialLogin = appContext.actions.socialLoginStore();
  const { email, statusDisplay, isSubmitting, isVerificationSent, fieldErrors } = state;

  const isLoading = isSubmitting;
  const socialState = socialLogin.getState();
  const isSocialLoading = socialState.isLoading;

  // Navigate to CheckEmailPage after verification email sent
  useEffect(() => {
    if (isVerificationSent) {
      navigate("/register/complete", {
        state: {
          which: "confirm",
          nextState: "/register",
          email,
        },
      });
    }
  }, [isVerificationSent, email, navigate]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }
    actions.submitEmailForVerification();
  };

  // Handle input changes (state transition handled automatically by Store)
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    actions.setEmail(e.target.value);
  };

  // Social signup handler
  const handleSocialSignup = async (provider: "google" | "apple") => {
    // Initialize OAuth config if not ready
    if (socialState.socialState.type === 'idle') {
      await socialLogin.actions.initialize();
    }

    // Map UI provider to SocialProvider enum
    const providerEnum = provider === "google" ? SocialProvider.Google : SocialProvider.Apple;

    // Initiate OAuth flow (will redirect to provider)
    await socialLogin.actions.initiateOAuth(providerEnum);
  };

  return (
    <AuthPageLayout title={T.register.startTradingTitle} variant="grid" maxWidth="7xl">
      <div className="welcome-grid grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Column - Registration Form */}
        <div className="register-form w-full max-w-md mx-auto animate-slide-up">
          {/* Clean card - no border */}
          <div
            className="rounded-2xl p-8 shadow-2xl shadow-purple-900/10 bg-card"
          >
            {/* Status message - show error variant only if NO field errors */}
            {statusDisplay.variant === 'error' && !fieldErrors && statusDisplay.tooltip && (
              <Alert variant="destructive" size="sm" className="mb-6">
                <AlertDescription>{statusDisplay.tooltip}</AlertDescription>
              </Alert>
            )}

            {/* Social signup error */}
            {socialState.errorMessage && (
              <Alert variant="destructive" size="sm" className="mb-4">
                <AlertDescription>{socialState.errorMessage}</AlertDescription>
              </Alert>
            )}

            {/* Social signup */}
            <div className="space-y-3 mb-6">
              <SocialButton
                provider="google"
                onClick={() => handleSocialSignup("google")}
                disabled={!!isLoading || !!isSocialLoading}
                size="lg"
              >
                Sign up with Google
              </SocialButton>

              <SocialButton
                provider="apple"
                onClick={() => handleSocialSignup("apple")}
                disabled={!!isLoading || !!isSocialLoading}
                size="lg"
              >
                Sign up with Apple
              </SocialButton>
            </div>

            {/* Divider */}
            <div className="my-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-background text-muted-foreground">{T.common.or}</span>
                </div>
              </div>
            </div>

            {/* Email registration form - only 1 field for maximum conversion */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <Field>
                <FieldLabel>{T.common.email}</FieldLabel>
                <Input
                  type="email"
                  placeholder={T.register.emailPlaceholderAlt}
                  value={email || ""}
                  onChange={handleEmailChange}
                  disabled={!!isLoading}
                  required
                  autoFocus
                  autoComplete="email"
                />
                {fieldErrors?.email && <FieldError>{fieldErrors.email}</FieldError>}
              </Field>

              {/* Orange CTA button */}
              <Button
                type="submit"
                disabled={isLoading}
                variant="default"
               
                size="lg"
                className="w-full text-base"
              >
                {statusDisplay.variant === 'info'
                  ? statusDisplay.formatted
                  : T.register.signUp}
              </Button>

              {/* Login link */}
              <div className="text-center">
                <Link to="/welcome" className="auth-link">
                  {T.register.alreadyHaveAccountLogin}
                </Link>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column - Hero Content with Feature Cards */}
        <div className="register-hero space-y-8">
          {/* Headline and subtitle */}
          <div className="space-y-4">
            <Text
              as="h1"
              size="6xl"
              weight="bold"
              className="leading-tight text-foreground"
            >
              {T.register.startTradingTitle}
            </Text>
            <Text size="xl" className="text-muted-foreground">
              {T.register.joinTraders}
            </Text>
            <Text size="sm" className="text-muted-foreground">
              {T.register.freeDemoNoCreditCard}
            </Text>
          </div>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-2 gap-6">
            <FeatureCard
              icon="chart"
              title={T.features.realTimeData}
              description={T.features.realTimeDataDesc}
            />
            <FeatureCard
              icon="orders"
              title={T.features.advancedOrders}
              description={T.features.advancedOrdersDesc}
            />
            <FeatureCard
              icon="customize"
              title={T.features.customizable}
              description={T.features.customizableDesc}
            />
            <FeatureCard
              icon="tools"
              title={T.welcome.professionalTools}
              description={T.welcome.professionalToolsDesc}
            />
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
}

// Main component - Store provider is handled by RouteRenderer
export default function RegisterPage() {
  return <RegisterPageContent />;
}
